import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Portal,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { groupReadinessWarnings } from '../utils/releaseReadiness';

/** Purpose: review and request releases to production. Why: production changes need a visible preflight, audit trail, and deployment workflow. */
export default function Deployments() {
  const navigate = useNavigate();
  const { selectedBot, selectedEnv, isAdmin, isBotAdmin } = useAuth();
  const superAdmin = isAdmin();
  const canManageDeployments = isBotAdmin(selectedBot);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestForm, setRequestForm] = useState({
    description: '',
    includeEnvironment: true,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [flows, setFlows] = useState([]);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [scenarioForm, setScenarioForm] = useState({ name: '', flowId: '', inputs: 'start', messagesContain: '', ended: '', mocks: '{}' });
  const [regressionLoading, setRegressionLoading] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('deployments-header-actions') : null
  ));
  const warningGroups = groupReadinessWarnings(readiness?.warnings || []);

  useEffect(() => {
    setHeaderHost(document.getElementById('deployments-header-actions'));
  }, []);

  useEffect(() => {
    setLoading(true);
    if (canManageDeployments) {
      fetchRequests();
      fetchReadiness();
      fetchRegressionData();
    } else {
      setLoading(false);
    }
  }, [selectedBot, canManageDeployments]);

  /** Purpose: Load the production preflight report for the selected bot. Why: Deployment decisions need current server validation. */
  const fetchReadiness = async () => {
    if (!selectedBot) return;
    try {
      const response = await axios.get('/api/v1/deployments/preflight', { params: { botId: selectedBot, environment: 'dev' } });
      setReadiness(response.data?.readiness || null);
    } catch (error) {
      setReadiness(error.response?.data?.readiness || null);
    }
  };

  /** Purpose: Load regression scenarios and the development environment. Why: Release checks must run against the correct source flow scope. */
  const fetchRegressionData = async () => {
    if (!selectedBot) return;
    try {
      const [scenarioResponse, environmentResponse] = await Promise.all([
        axios.get('/api/v1/flow-regressions', { params: { botId: selectedBot, env: 'dev' } }),
        axios.get(`/api/v1/environments/${selectedBot}`),
      ]);
      /** Purpose: Resolve the development environment from available environments. Why: Regression authoring uses development data as its source. */
      const devEnvironment = (environmentResponse.data?.environments || []).find((item) => item.envName === 'dev');
      const flowResponse = devEnvironment
        ? await axios.get('/api/v1/flows', { params: { botId: selectedBot, environmentId: devEnvironment.envId } })
        : { data: { flows: [] } };
      setScenarios(scenarioResponse.data?.scenarios || []);
      setFlows(flowResponse.data?.flows || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Failed to load regression scenarios' });
    }
  };

  /** Purpose: Validate and persist a flow regression scenario. Why: Mocks must be valid JSON before a test case is stored. */
  const saveScenario = async () => {
    if (!scenarioForm.name.trim() || !scenarioForm.flowId) return;
    setRegressionLoading(true);
    try {
      let mocks;
      try { mocks = JSON.parse(scenarioForm.mocks || '{}'); } catch (_) { throw new Error('Node mocks must be valid JSON.'); }
      await axios.post('/api/v1/flow-regressions', {
        botId: selectedBot,
        env: 'dev',
        name: scenarioForm.name.trim(),
        flowId: scenarioForm.flowId,
        inputs: scenarioForm.inputs.split('\n').map((message) => message.trim()).filter(Boolean).map((message) => ({ message })),
        expectations: {
          messagesContain: scenarioForm.messagesContain.split('\n').map((message) => message.trim()).filter(Boolean),
          ...(scenarioForm.ended === '' ? {} : { ended: scenarioForm.ended === 'true' }),
        },
        mocks,
      });
      setScenarioDialogOpen(false);
      setScenarioForm({ name: '', flowId: '', inputs: 'start', messagesContain: '', ended: '', mocks: '{}' });
      setMessage({ type: 'success', text: 'Regression scenario saved. Run it before requesting a deployment.' });
      await fetchRegressionData();
      await fetchReadiness();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save scenario' });
    } finally { setRegressionLoading(false); }
  };

  /** Purpose: Run one saved regression scenario and report its result. Why: Operators need to verify a critical customer path before release. */
  const runScenario = async (scenarioId) => {
    setRegressionLoading(true);
    try {
      const response = await axios.post(`/api/v1/flow-regressions/${scenarioId}/run`, { botId: selectedBot, env: 'dev' });
      const result = response.data?.result;
      setMessage({ type: result?.status === 'passed' ? 'success' : 'error', text: result?.status === 'passed' ? 'Regression scenario passed.' : `Scenario ${result?.status || 'failed'}: ${result?.failures?.[0] || 'Review the result.'}` });
      await fetchRegressionData();
      await fetchReadiness();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Scenario run failed' });
    } finally { setRegressionLoading(false); }
  };

  /** Purpose: Run the full regression suite and summarize results. Why: Release readiness depends on the combined outcome, not one selected case. */
  const runAllScenarios = async () => {
    setRegressionLoading(true);
    try {
      const response = await axios.post('/api/v1/flow-regressions/run-all', { botId: selectedBot, env: 'dev' });
      const summary = response.data?.summary || {};
      setMessage({ type: summary.failed || summary.blocked ? 'error' : 'success', text: `${summary.passed || 0}/${summary.total || 0} scenarios passed${summary.blocked ? ` · ${summary.blocked} blocked` : ''}.` });
      await fetchRegressionData();
      await fetchReadiness();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Regression run failed' });
    } finally { setRegressionLoading(false); }
  };

  /** Purpose: Confirm and remove a regression scenario. Why: Accidental deletion should be prevented and scoped to the selected bot/environment. */
  const deleteScenario = async (scenarioId) => {
    if (!window.confirm('Delete this regression scenario?')) return;
    setRegressionLoading(true);
    try {
      await axios.delete(`/api/v1/flow-regressions/${scenarioId}`, { params: { botId: selectedBot, env: 'dev' } });
      await fetchRegressionData();
      await fetchReadiness();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Failed to delete scenario' });
    } finally { setRegressionLoading(false); }
  };

  /** Purpose: Load deployment requests for the authorized bot scope. Why: Approval views should not expose unrelated bot requests. */
  const fetchRequests = async () => {
    try {
      const response = await axios.get('/api/v1/deployments/requests', {
        params: superAdmin ? undefined : { botId: selectedBot },
      });
      setRequests(response.data.requests || []);
    } catch (error) {
      if (error.response?.data?.readiness) setReadiness(error.response.data.readiness);
      console.error('Error fetching requests:', error);
      setMessage({ type: 'error', text: 'Failed to fetch deployment requests' });
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Create a production deployment request after validating bot selection. Why: Publishing changes require an explicit reviewable request. */
  const handleRequestDeployment = async () => {
    if (!selectedBot) {
      setMessage({ type: 'error', text: 'Please select a bot first' });
      return;
    }

    setActionLoading(true);
    setMessage(null);

    try {
      await axios.post('/api/v1/deployments/request', {
        botId: selectedBot,
        includeEnvironment: requestForm.includeEnvironment,
        description: requestForm.description,
      });
      setMessage({ type: 'success', text: 'Deployment request created. Approve it before deploying to PROD.' });
      setRequestDialogOpen(false);
      setRequestForm({ description: '', includeEnvironment: true });
      if (canManageDeployments) {
        fetchRequests();
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to create deployment request' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Purpose: Approve a deployment request and refresh the request list. Why: Approval must be reflected from the server's recorded state. */
  const handleApprove = async (requestId) => {
    setActionLoading(true);
    setMessage(null);

    try {
      await axios.post(`/api/v1/deployments/requests/${requestId}/approve`);
      setMessage({ type: 'success', text: 'Deployment request approved!' });
      fetchRequests();
      setApprovalDialogOpen(false);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to approve request' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Purpose: Reject a deployment request with the supplied reason. Why: Release audit history needs the decision context. */
  const handleReject = async (requestId, reason) => {
    setActionLoading(true);
    setMessage(null);

    try {
      await axios.post(`/api/v1/deployments/requests/${requestId}/reject`, { reason });
      setMessage({ type: 'success', text: 'Deployment request rejected!' });
      fetchRequests();
      setApprovalDialogOpen(false);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to reject request' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Purpose: Confirm and execute a production deployment. Why: This operation overwrites production data and therefore requires explicit confirmation. */
  const handleDeploy = async (requestId) => {
    if (!window.confirm('Are you sure you want to deploy this to production? This will overwrite existing production data.')) {
      return;
    }

    setActionLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`/api/v1/deployments/requests/${requestId}/deploy`);
      const deployedFlowCount = Number(response.data?.deployed?.flows || 0);
      const localizationKeys = Number(response.data?.deployed?.localizationKeys || 0);
      setMessage({ type: 'success', text: `Deployment completed successfully. ${deployedFlowCount} flow(s) and ${localizationKeys} localization key(s) promoted to PROD.` });
      fetchRequests();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to deploy' 
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Purpose: Map deployment lifecycle states to visual severities. Why: Request status should be recognizable at a glance. */
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'info',
      rejected: 'error',
      deployed: 'success',
    };
    return colors[status] || 'default';
  };

  /** Purpose: Open the approval dialog for the selected request. Why: Review actions need a clear target. */
  const openApprovalDialog = (request) => {
    setSelectedRequest(request);
    setApprovalDialogOpen(true);
  };

  /** Purpose: Fetch the full request record and show its details. Why: The table summary is insufficient for deployment review. */
  const openDetailsDialog = async (requestId) => {
    try {
      const response = await axios.get(`/api/v1/deployments/requests/${requestId}`);
      setSelectedRequest(response.data.request);
      setDetailsDialogOpen(true);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load request details' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Portal container={headerHost} disablePortal={!headerHost}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: headerHost ? '100%' : 'auto',
          flex: headerHost ? 1 : undefined,
          minWidth: 0,
          mb: headerHost ? 0 : 3,
        }}>
          <Typography variant={headerHost ? 'h6' : 'h4'} sx={{ flexShrink: 0, fontWeight: 750 }}>
            Deployments
          </Typography>
          {selectedBot && selectedEnv === 'dev' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setRequestDialogOpen(true)}
              sx={{ ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Request Deployment
            </Button>
          )}
        </Box>
      </Portal>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {readiness && (
        <Alert severity={readiness.valid ? 'success' : 'error'} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Release preflight: {readiness.valid ? 'Ready' : `${readiness.errors?.length || 0} blocking issue(s)`}
            {readiness.warnings?.length ? ` · ${readiness.warnings.length} warning(s)` : ''}
          </Typography>
          {!readiness.valid && Array.isArray(readiness.errors) && readiness.errors.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {readiness.errors.map((err, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 0.75,
                    pt: idx > 0 ? 0.5 : 0,
                  }}
                >
                  <Typography variant="body2" sx={{ display: 'block' }}>
                    • {err.message}
                  </Typography>
                  {err.flowId && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => navigate(`/flows/${err.flowId}${err.nodeId ? `?nodeId=${err.nodeId}` : ''}`)}
                      sx={{
                        textTransform: 'none',
                        py: 0.25,
                        px: 1.25,
                        fontSize: '0.75rem',
                        fontWeight: 650,
                        borderRadius: '6px',
                        height: 26,
                        borderColor: 'currentColor',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: 'currentColor',
                        },
                      }}
                    >
                      Edit Flow
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {Array.isArray(readiness.warnings) && readiness.warnings.length > 0 && (
            <Box sx={{ mt: 1.25, pt: 0.5, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => setShowWarnings((prev) => !prev)}
                sx={{
                  textTransform: 'none',
                  p: 0,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  opacity: 0.9,
                  minWidth: 0,
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                }}
              >
                {showWarnings
                  ? '▲ Hide non-blocking warnings'
                  : `▼ View ${readiness.warnings.length} warnings · ${warningGroups.length} distinct finding${warningGroups.length === 1 ? '' : 's'}`}
              </Button>
              {showWarnings && (
                <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid rgba(255, 255, 255, 0.25)' }}>
                  {warningGroups.map((warn, idx) => (
                    <Box
                      key={`${warn.code || 'warning'}-${warn.resourceId || ''}-${warn.flowId || ''}-${warn.nodeId || ''}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 0.6,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ display: 'block', fontSize: '0.8rem', opacity: 0.95 }}>
                          • {warn.message}
                        </Typography>
                        {warn.resourceType && (
                          <Chip size="small" label={warn.resourceType} sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.16)', color: 'inherit', fontSize: '0.68rem' }} />
                        )}
                        {warn.count > 1 && (
                          <Chip size="small" label={`Repeated ${warn.count}×`} sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.16)', color: 'inherit', fontSize: '0.68rem' }} />
                        )}
                      </Box>
                      {warn.flowId && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => navigate(`/flows/${warn.flowId}${warn.nodeId ? `?nodeId=${warn.nodeId}` : ''}`)}
                          sx={{
                            textTransform: 'none',
                            py: 0.1,
                            px: 1,
                            fontSize: '0.7rem',
                            fontWeight: 650,
                            borderRadius: '4px',
                            height: 24,
                            borderColor: 'currentColor',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              borderColor: 'currentColor',
                            },
                          }}
                        >
                          Edit Flow
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Alert>
      )}

      {!canManageDeployments && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only this bot's admin can view, approve, and deploy requests. You can request a deployment if you have DEV access.
        </Alert>
      )}

      {canManageDeployments && (
        <Paper variant="outlined" sx={{ mb: 3, p: 2.5, borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <ScienceOutlinedIcon color="primary" />
                <Typography variant="h6">Flow regression tests</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Repeat critical journeys before release. External API, database, AI and handoff steps are blocked in safe mode.</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="outlined" startIcon={<PlayArrowIcon />} onClick={runAllScenarios} disabled={regressionLoading || !scenarios.length}>Run all</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setScenarioDialogOpen(true)} disabled={!flows.length}>New scenario</Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          {!scenarios.length ? (
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {flows.length
                    ? 'No release checks are configured yet. Add a representative conversation to catch regressions before deployment.'
                    : 'Create a flow first, then add a regression scenario to verify its key customer journey before release.'}
                </Typography>
              </Box>
              {flows.length ? (
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setScenarioDialogOpen(true)}>
                  Create first scenario
                </Button>
              ) : (
                <Button size="small" variant="outlined" onClick={() => navigate('/flows')}>
                  Open Journey Studio
                </Button>
              )}
            </Box>
          ) : scenarios.map((scenario) => (
            <Box key={scenario.scenarioId} display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>{scenario.name}</Typography>
                <Typography variant="caption" color="text.secondary">{flows.find((flow) => flow.flowId === scenario.flowId)?.name || scenario.flowId} · {scenario.inputs?.length || 0} turn(s){scenario.lastRun?.failures?.[0] ? ` · ${scenario.lastRun.failures[0]}` : ''}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Chip size="small" label={scenario.lastRun?.status || 'never'} color={scenario.lastRun?.status === 'passed' ? 'success' : scenario.lastRun?.status === 'never' ? 'default' : 'error'} />
                <Tooltip title="Run"><span><IconButton onClick={() => runScenario(scenario.scenarioId)} disabled={regressionLoading}><PlayArrowIcon /></IconButton></span></Tooltip>
                <Tooltip title="Delete"><span><IconButton color="error" onClick={() => deleteScenario(scenario.scenarioId)} disabled={regressionLoading}><DeleteOutlineIcon /></IconButton></span></Tooltip>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Bot</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Changes</TableCell>
              <TableCell>Created</TableCell>
              {canManageDeployments && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManageDeployments ? 8 : 7} align="center">
                  No deployment requests found
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request.requestId}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {request.requestId.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {request.deploymentVersion ? (
                      <Chip label={`v${request.deploymentVersion}`} color="primary" size="small" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>{request.bot?.botName || request.botId}</TableCell>
                  <TableCell>
                    {request.requestedBy?.name || request.requestedBy?.email || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      +{request.changesSummary?.flowsAdded || 0} / 
                      ~{request.changesSummary?.flowsUpdated || 0} / 
                      -{request.changesSummary?.flowsDeleted || 0}
                    </Typography>
                    {request.changesSummary?.configChanged && (
                      <Chip label="Config" size="small" sx={{ ml: 1 }} />
                    )}
                    {request.changesSummary?.localizationKeys > 0 && (
                      <Chip label={`${request.changesSummary.localizationKeys} localization keys`} size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {format(new Date(request.createdAt), 'PPp')}
                  </TableCell>
                  {canManageDeployments && (
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openDetailsDialog(request.requestId)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {request.status === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => openApprovalDialog(request)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const reason = prompt('Rejection reason:');
                                if (reason) handleReject(request.requestId, reason);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {request.status === 'approved' && (
                        <Tooltip title="Deploy to Production">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDeploy(request.requestId)}
                            disabled={actionLoading}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Request Deployment Dialog */}
      <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Deployment to Production</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            value={requestForm.description}
            onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
            margin="normal"
            placeholder="Describe what changes you're deploying..."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={requestForm.includeEnvironment}
                onChange={(e) => setRequestForm({ ...requestForm, includeEnvironment: e.target.checked })}
              />
            }
            label="Include setting-level configuration & bot languages"
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
            Copies intent thresholds, bot languages, AI prompt, voice, widget, and channel settings from DEV to PROD.
          </Typography>
          <Alert severity="info" variant="outlined" sx={{ mt: 2, py: 0, fontSize: '0.8rem' }}>
            This request and its approval/deployment status changes are recorded in the workspace audit trail.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRequestDeployment}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Request Deployment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scenarioDialogOpen} onClose={() => setScenarioDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New flow regression scenario</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Scenario name" margin="normal" value={scenarioForm.name} onChange={(event) => setScenarioForm({ ...scenarioForm, name: event.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Flow</InputLabel>
            <Select label="Flow" value={scenarioForm.flowId} onChange={(event) => setScenarioForm({ ...scenarioForm, flowId: event.target.value })}>
              {flows.map((flow) => <MenuItem key={flow.flowId} value={flow.flowId}>{flow.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth multiline minRows={3} label="Node mocks (optional JSON)" margin="normal"
            value={scenarioForm.mocks} onChange={(event) => setScenarioForm({ ...scenarioForm, mocks: event.target.value })}
            helperText={'Example: {"api-node-id":{"branch":"success","result":{"status":"ok"},"variables":{"case_id":"123"}}}'}
          />
          <TextField fullWidth multiline minRows={3} label="Conversation turns (one per line)" margin="normal" value={scenarioForm.inputs} onChange={(event) => setScenarioForm({ ...scenarioForm, inputs: event.target.value })} helperText="First line starts the journey. Add later lines as replies to Question or Quick Reply nodes." />
          <TextField fullWidth multiline minRows={2} label="Expected bot text (one phrase per line)" margin="normal" value={scenarioForm.messagesContain} onChange={(event) => setScenarioForm({ ...scenarioForm, messagesContain: event.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Expected journey state</InputLabel>
            <Select label="Expected journey state" value={scenarioForm.ended} onChange={(event) => setScenarioForm({ ...scenarioForm, ended: event.target.value })}>
              <MenuItem value="">Do not check</MenuItem><MenuItem value="true">Ended</MenuItem><MenuItem value="false">Waiting / active</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions><Button onClick={() => setScenarioDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={saveScenario} disabled={regressionLoading || !scenarioForm.name.trim() || !scenarioForm.flowId || !scenarioForm.inputs.trim()}>Save scenario</Button></DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)}>
        <DialogTitle>Approve Deployment Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Bot:</strong> {selectedRequest.bot?.botName || selectedRequest.botId}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Requested by:</strong> {selectedRequest.requestedBy?.name || selectedRequest.requestedBy?.email}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Changes:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                • Flows added: {selectedRequest.changesSummary?.flowsAdded || 0}
                <br />
                • Flows updated: {selectedRequest.changesSummary?.flowsUpdated || 0}
                <br />
                • Flows deleted: {selectedRequest.changesSummary?.flowsDeleted || 0}
                <br />
                • Config changed: {selectedRequest.changesSummary?.configChanged ? 'Yes' : 'No'}
                <br />
                • Localization keys included: {selectedRequest.changesSummary?.localizationKeys || 0}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleApprove(selectedRequest?.requestId)}
            variant="contained"
            color="success"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Deployment Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="h6" gutterBottom>Request Information</Typography>
              <Typography variant="body2"><strong>Request ID:</strong> {selectedRequest.requestId}</Typography>
              <Typography variant="body2"><strong>Bot:</strong> {selectedRequest.bot?.botName || selectedRequest.botId}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {selectedRequest.status}</Typography>
              <Typography variant="body2"><strong>Requested by:</strong> {selectedRequest.requestedBy?.name || selectedRequest.requestedBy?.email}</Typography>
              {selectedRequest.approvedBy && (
                <Typography variant="body2"><strong>Approved by:</strong> {selectedRequest.approvedBy?.name || selectedRequest.approvedBy?.email}</Typography>
              )}
              {selectedRequest.deploymentDetails?.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Description:</strong> {selectedRequest.deploymentDetails.description}
                </Typography>
              )}
              
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Changes Summary</Typography>
              <Typography variant="body2">• Flows to deploy: {selectedRequest.deploymentDetails?.flows?.length || 0}</Typography>
              <Typography variant="body2">• Flows added: {selectedRequest.changesSummary?.flowsAdded || 0}</Typography>
              <Typography variant="body2">• Flows updated: {selectedRequest.changesSummary?.flowsUpdated || 0}</Typography>
              <Typography variant="body2">• Flows deleted: {selectedRequest.changesSummary?.flowsDeleted || 0}</Typography>
              <Typography variant="body2">• Environment config: {selectedRequest.deploymentDetails?.environment ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">• Config changed: {selectedRequest.changesSummary?.configChanged ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2">• Localization keys included: {selectedRequest.changesSummary?.localizationKeys || 0}</Typography>

              {selectedRequest.flows && selectedRequest.flows.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Flows</Typography>
                  {selectedRequest.flows.map((flow) => (
                    <Typography key={flow.flowId} variant="body2">
                      • {flow.name} ({flow.flowId})
                    </Typography>
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
